import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  DatePicker,
  Table,
  Spin,
  Divider,
  Segmented,
  Switch,
  Modal
} from 'antd';
import { Map, useMap, Marker } from '@vis.gl/react-google-maps';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import 'chartjs-plugin-zoom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudDownload } from '@fortawesome/pro-duotone-svg-icons';
import Button from '../../../shared-components/buttons/Button';
import {
  userPrivilagesAtom,
  notificationsAtom,
  amaDashboardShowMphAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { convertKmhToMph, percentage, thousand, toLocalTime } from '../../../../functions';

function AmaDashboardAnalytics() {
  const { t, i18n } = useTranslation();
  const googleMap = useMap();
  const { RangePicker } = DatePicker;
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const showMph = useRecoilValue(amaDashboardShowMphAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [loadingDeviceCommunication, setLoadingDeviceCommunication] = useState(false);
  const [loadingExportDeviceCommunication, setExportLoadingDeviceCommunication] = useState(false);
  const [deviceComminucationData, setDeviceComminucationData] = useState([]);
  const [loadingGpsDeviceCommunication, setLoadingGpsDeviceCommunication] = useState(false);
  const [loadingExportGpsDeviceCommunication, setLoadingExportGpsDeviceCommunication] = useState(false);
  const [deviceGpsComminucationData, setDeviceGpsComminucationData] = useState([]);
  const [selectedSpeedingGrouping, setSelectedSpeedingGrouping] = useState(t('days'));
  const [loadingSpeedingData, setLoadingSpeedingData] = useState(false);
  const [loadingExportSpeedingData, setLoadingExportSpeedingData] = useState(false);
  const [selectedSpeedingDateRange, setSelectedSpeedingDateRange] = useState({
    start: dayjs().startOf('week').format('MMM DD, YYYY'),
    end: dayjs().endOf('week').format('MMM DD, YYYY')
  });
  const [speedingData, setSpeedingData] = useState({
    days: [],
    devices: [],
    severities: []
  });

  const [selectedDelayedDepartureGrouping, setSelectedDelayedDepartureGrouping] = useState(t('days'));
  const [selectedDelayedDepartureDateRange, setSelectedDelayedDepartureDateRange] = useState({
    start: dayjs().startOf('week').format('MMM DD, YYYY'),
    end: dayjs().endOf('week').format('MMM DD, YYYY')
  });
  const [loadingDelayedDeparturesData, setLoadingDelayedDeparturesData] = useState(false);
  const [loadingExportDelayedDeparturesData, setLoadingExportDelayedDeparturesData] = useState(false);
  const [viewDelayedDeparturePercentage, setViewDelayedDeparturePercentage] = useState(false);
  const [delayedDeparturesData, setDelayedDeparturesData] = useState({
    days: [],
    routes: []
  });

  const [selectedArrivalDepartureDateRange, setSelectedArrivalDepartureDateRange] = useState({
    start: dayjs().startOf('week').format('MMM DD, YYYY'),
    end: dayjs().endOf('week').format('MMM DD, YYYY')
  });
  const [loadingArrivalDeparturesData, setLoadingArrivalDeparturesData] = useState(false);
  const [loadingExportArrivalDeparturesData, setLoadingExportArrivalDeparturesData] = useState(false);
  const [arrivalDeparturesData, setArrivalDeparturesData] = useState([]);

  const [selectedCoords, setSelectedCoords] = useState({ lon: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState(15);

  function getDeviceCommunication() {
    setLoadingDeviceCommunication(true);
    api.get('ama/dashboard/devices/communication')
    .then((response) => {
      if (response.data.success) {
        setDeviceComminucationData(response.data.data);
      } else {
        setDeviceComminucationData([]);
      }
    })
    .catch((error) => {
      setDeviceComminucationData([]);
    })
    .finally(() => {
      setLoadingDeviceCommunication(false);
    });
  }

  function getGpsDeviceCommunication() {
    setLoadingGpsDeviceCommunication(true);
    api.get('ama/dashboard/devices/communication/gps')
    .then((response) => {
      if (response.data.success) {
        setDeviceGpsComminucationData(response.data.data);
      } else {
        setDeviceGpsComminucationData([]);
      }
    })
    .catch((error) => {
      setDeviceGpsComminucationData([]);
    })
    .finally(() => {
      setLoadingGpsDeviceCommunication(false);
    });
  }

  function getDelayedDeparturesData() {
    const startDate = dayjs(selectedDelayedDepartureDateRange.start);
    const endDate = dayjs(selectedDelayedDepartureDateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    setLoadingDelayedDeparturesData(true);
    api.get(`ama/dashboard/delayed/departures?startDate=${startString}&endDate=${endString}`)
    .then((response) => {
      if (response.data.success) {
        setDelayedDeparturesData(response.data.data);
      } else {
        setDelayedDeparturesData({
          days: [],
          routes: []
        });
      }
    })
    .catch((error) => {
      setDelayedDeparturesData({
        days: [],
        routes: []
      });
    })
    .finally(() => {
      setLoadingDelayedDeparturesData(false);
    });
  }

  function getSpeedingData() {
    const startDate = dayjs(selectedSpeedingDateRange.start);
    const endDate = dayjs(selectedSpeedingDateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    setLoadingSpeedingData(true);
    api.get(`ama/dashboard/excess/speed?startDate=${startString}&endDate=${endString}`)
    .then((response) => {
      if (response.data.success) {
        setSpeedingData(response.data.data);
      } else {
        setSpeedingData({
          days: [],
          devices: [],
          severities: []
        });
      }
    })
    .catch((error) => {
      setSpeedingData({
        days: [],
        devices: [],
        severities: []
      });
    })
    .finally(() => {
      setLoadingSpeedingData(false);
    });
  }

  function getDeviceCommunicationExport() {
    setExportLoadingDeviceCommunication(true);
    api.get('ama/dashboard/devices/communication/export', { responseType: 'blob' })
    .then((response) => {
      const fileName = 'device_communication_export.xlsx';
      const href = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    })
    .catch((error) => {
      setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
    })
    .finally(() => {
      setExportLoadingDeviceCommunication(false);
    });
  }

  function getGpsDeviceCommunicationExport() {
    setLoadingExportGpsDeviceCommunication(true);
    api.get('ama/dashboard/devices/communication/gps/export', { responseType: 'blob' })
    .then((response) => {
      const fileName = 'gps_communication_export.xlsx';
      const href = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    })
    .catch((error) => {
      setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
    })
    .finally(() => {
      setLoadingExportGpsDeviceCommunication(false);
    });
  }

  function getDelayedDeparturesDataExport() {
    const startDate = dayjs(selectedDelayedDepartureDateRange.start);
    const endDate = dayjs(selectedDelayedDepartureDateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    setLoadingExportDelayedDeparturesData(true);
    api.get(`ama/dashboard/delayed/departures/export?startDate=${startString}&endDate=${endString}`, { responseType: 'blob' })
    .then((response) => {
      const fileName = 'delayed_departures_export.xlsx';
      const href = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    })
    .catch((error) => {
      setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
    })
    .finally(() => {
      setLoadingExportDelayedDeparturesData(false);
    });
  }

  function getSpeedingDataExport() {
    const startDate = dayjs(selectedSpeedingDateRange.start);
    const endDate = dayjs(selectedSpeedingDateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    setLoadingExportSpeedingData(true);
    api.get(`ama/dashboard/excess/speed/export?startDate=${startString}&endDate=${endString}`, { responseType: 'blob' })
    .then((response) => {
      const fileName = 'excess_speed_export.xlsx';
      const href = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    })
    .catch((error) => {
      setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
    })
    .finally(() => {
      setLoadingExportSpeedingData(false);
    });
  }

  function getSpeedingTableData() {
    if (selectedSpeedingGrouping === t('days')) {
      return speedingData.days;
    }
    if (selectedSpeedingGrouping === t('devices')) {
      return speedingData.devices;
    }
    return speedingData.severities;
  }

  function getArrivalDeparturesData() {
    setLoadingArrivalDeparturesData(true);
    const startDate = dayjs(selectedArrivalDepartureDateRange.start);
    const endDate = dayjs(selectedArrivalDepartureDateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.get(`ama/dashboard/terminal/arrivalexits?startDate=${startString}&endDate=${endString}`)
    .then((response) => {
      if (response.data.success) {
        setArrivalDeparturesData(response.data.data);
      } else {
        setArrivalDeparturesData([]);
      }
    })
    .catch((error) => {
      setArrivalDeparturesData([]);
    })
    .finally(() => {
      setLoadingArrivalDeparturesData(false);
    });
  }

  function getArrivalDeparturesDataExport() {
    const startDate = dayjs(selectedArrivalDepartureDateRange.start);
    const endDate = dayjs(selectedArrivalDepartureDateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    setLoadingExportArrivalDeparturesData(true);
    api.get(`ama/dashboard/terminal/arrivalexits/export?startDate=${startString}&endDate=${endString}`, { responseType: 'blob' })
    .then((response) => {
      const fileName = 'arrival_vs_departures_export.xlsx';
      const href = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    })
    .catch((error) => {
      setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
    })
    .finally(() => {
      setLoadingExportArrivalDeparturesData(false);
    });
  }

  function refreshData() {
    getDeviceCommunication();
    getGpsDeviceCommunication();
    getSpeedingData();
    getDelayedDeparturesData();
    getArrivalDeparturesData();
  }

  function getDoughnutColors(list) {
    const red = 14;
    const green = 115;
    const blue = 59;
    const alpha = 170;
    const colors = [];
    const orderedList = _.orderBy(list, (i) => i.count, 'desc')
    for (let i = 0; i < orderedList.length; i++) {
      const modifier = (i + 1) * 15;
      colors.push(`rgba(${red + modifier + 10},${green + modifier},${blue},${alpha - modifier})`);
    }
    return colors;
  }

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    getSpeedingData();
  }, [selectedSpeedingDateRange]);

  useEffect(() => {
    getDelayedDeparturesData();
  }, [selectedDelayedDepartureDateRange]);

  useEffect(() => {
    getArrivalDeparturesData();
  }, [selectedArrivalDepartureDateRange]);

  useEffect(() => {
    if (selectedCoords.lon !== 0 && selectedCoords.lng !== 0) {
      setMapZoom(16);
    }
  }, [selectedCoords]);

  return (
    <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-6 mb-8">
      <div className="col-span-1 md:col-span-2 lg:col-span-6">
        <div className="flex">
          <Button
            className="bg-primaryColor ml-auto"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('refresh')}
            loading={loadingDeviceCommunication || loadingGpsDeviceCommunication || loadingSpeedingData}
            onClick={() => refreshData()}
          />
        </div>
      </div>
      <Divider className="col-span-1 md:col-span-2 lg:col-span-6" orientation="left">{t('device_communication')}</Divider>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 flex">
        <Button
          className="bg-primaryColor px-2 ml-auto"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={(
            <div>
              <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
              {t('export')}
            </div>
          )}
          loading={loadingDeviceCommunication || loadingExportDeviceCommunication}
          onClick={() => getDeviceCommunicationExport()}
        />
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 lg:col-span-2 h-96 lg:max-h-96">
        {loadingDeviceCommunication && (
          <div className="flex flex-col content-center">
            <Spin className="w-full h-full mt-32" spinning={loadingDeviceCommunication} />
          </div>
        )}
        {!loadingDeviceCommunication && (
          <Doughnut
            data={{
              labels: _.map(deviceComminucationData, (s) => s.label),
              datasets: [
                {
                  label: t('count'),
                  borderColor: '#0e733b',
                  backgroundColor: getDoughnutColors(deviceComminucationData),
                  borderWidth: 1,
                  data: _.map(deviceComminucationData, (x) => x.count),
                }
              ],
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                  labels: {
                    font: {
                      size: 20
                    }
                  }
                },
                title: {
                  display: false,
                },
                datalabels: {
                  color: 'black',
                  formatter: (value, context) => thousand(value),
                  labels: {
                    value: {
                      font: {
                        size: 20
                      }
                    }
                  }
                }
              },
            }}
          />
        )}
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 lg:col-span-4">
        <Table
          dataSource={deviceComminucationData}
          bordered
          loading={loadingDeviceCommunication}
          rowKey="label"
          columns={[
            {
              title: '',
              align: 'left',
              className: 'text-sm w-full md:hidden',
              render: (row) => (
                <div className="grid grid-cols-2 gap-2 w-full text-center">
                  <div className="col-span-1 text-left">
                    <p className="font-bold">{t('category')}</p>
                    <p>{row.label}</p>
                  </div>
                  <div className="col-span-1 text-right">
                    <p className="font-bold">{t('count')}</p>
                    <p>{thousand(row.count)}</p>
                  </div>
                </div>
              )
            },
            {
              title: t('category'),
              key: 'category',
              align: 'center',
              sorter: (a, b) => a?.label?.localeCompare(b?.label),
              className: 'text-sm hidden md:table-cell',
              render: (row) => (
                <span>{row.label}</span>
              ),
            },
            {
              title: t('count'),
              key: 'count',
              align: 'center',
              sorter: (a, b) => a?.count - b?.count,
              className: 'text-sm hidden md:table-cell',
              render: (row) => (
                <span>{thousand(row.count)}</span>
              ),
            },
          ]}
          expandable={{
            expandRowByClick: false,
            rowExpandable: (record) => record.devices?.length > 0,
            expandedRowRender: (record, index) => (
              <Table
                dataSource={record.devices}
                bordered
                size="small"
                columns={[
                  {
                    title: '',
                    align: 'left',
                    className: 'text-sm w-full md:hidden',
                    render: (row) => (
                      <div className="grid grid-cols-2 gap-2 w-full text-center">
                        <div className="col-span-1 text-left">
                          <p className="font-bold">{t('name')}</p>
                          <p>{row.deviceName}</p>
                          <p className="text-xs text-gray-500 italic">{row.deviceComment}</p>
                        </div>
                        <div className="col-span-1 text-right">
                          <p className="font-bold">{t('days_without_communication')}</p>
                          <p>{row.daysWithoutCommunication ? thousand(row.daysWithoutCommunication) : '0'}</p>
                        </div>
                      </div>
                    )
                  },
                  {
                    title: t('name'),
                    key: 'name',
                    align: 'center',
                    sorter: (a, b) => a?.deviceName?.localeCompare(b?.deviceName),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.deviceName}</span>
                    ),
                  },
                  {
                    title: t('comment'),
                    key: 'comment',
                    align: 'center',
                    sorter: (a, b) => a?.deviceComment?.localeCompare(b?.deviceComment),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.deviceComment}</span>
                    ),
                  },
                  {
                    title: t('days_without_communication'),
                    key: 'DaysWithoutCommunication',
                    align: 'center',
                    sorter: (a, b) => a?.daysWithoutCommunication - b?.daysWithoutCommunication,
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.daysWithoutCommunication ? thousand(row.daysWithoutCommunication) : '0'}</span>
                    ),
                  },
                ]}
              />
            )
          }}
        />
      </div>

      <Divider className="col-span-1 md:col-span-2 lg:col-span-6 pt-12" orientation="left">{t('gps_communication')}</Divider>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 flex">
        <Button
          className="bg-primaryColor px-2 ml-auto"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={(
            <div>
              <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
              {t('export')}
            </div>
          )}
          loading={loadingGpsDeviceCommunication || loadingExportGpsDeviceCommunication}
          onClick={() => getGpsDeviceCommunicationExport()}
        />
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 lg:col-span-2 h-96 lg:max-h-96">
        {loadingGpsDeviceCommunication && (
          <div className="flex flex-col content-center">
            <Spin className="w-full h-full mt-32" spinning={loadingGpsDeviceCommunication} />
          </div>
        )}
        {!loadingGpsDeviceCommunication && (
          <Doughnut
            data={{
              labels: _.map(deviceGpsComminucationData, (s) => s.label),
              datasets: [
                {
                  label: t('count'),
                  borderColor: '#0e733b',
                  backgroundColor: getDoughnutColors(deviceGpsComminucationData),
                  borderWidth: 1,
                  data: _.map(deviceGpsComminucationData, (x) => x.count),
                }
              ],
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                  labels: {
                    font: {
                      size: 20
                    }
                  }
                },
                title: {
                  display: false,
                },
                datalabels: {
                  color: 'black',
                  formatter: (value, context) => thousand(value),
                  labels: {
                    value: {
                      font: {
                        size: 20
                      }
                    }
                  }
                }
              },
            }}
          />
        )}
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 lg:col-span-4">
        <Table
          dataSource={deviceGpsComminucationData}
          bordered
          loading={loadingGpsDeviceCommunication}
          rowKey="label"
          columns={[
            {
              title: '',
              align: 'left',
              className: 'text-sm w-full md:hidden',
              render: (row) => (
                <div className="grid grid-cols-2 gap-2 w-full text-center">
                  <div className="col-span-1 text-left">
                    <p className="font-bold">{t('category')}</p>
                    <p>{row.label}</p>
                  </div>
                  <div className="col-span-1 text-right">
                    <p className="font-bold">{t('count')}</p>
                    <p>{thousand(row.count)}</p>
                  </div>
                </div>
              )
            },
            {
              title: t('category'),
              key: 'category',
              align: 'center',
              sorter: (a, b) => a?.label?.localeCompare(b?.label),
              className: 'text-sm hidden md:table-cell',
              render: (row) => (
                <span>{row.label}</span>
              ),
            },
            {
              title: t('count'),
              key: 'count',
              align: 'center',
              sorter: (a, b) => a?.count - b?.count,
              className: 'text-sm hidden md:table-cell',
              render: (row) => (
                <span>{thousand(row.count)}</span>
              ),
            },
          ]}
          expandable={{
            expandRowByClick: false,
            rowExpandable: (record) => record.devices?.length > 0,
            expandedRowRender: (record, index) => (
              <Table
                dataSource={record.devices}
                bordered
                size="small"
                columns={[
                  {
                    title: '',
                    align: 'left',
                    className: 'text-sm w-full md:hidden',
                    render: (row) => (
                      <div className="grid grid-cols-2 gap-2 w-full text-center">
                        <div className="col-span-1 text-left">
                          <p className="font-bold">{t('name')}</p>
                          <p>{row.deviceName}</p>
                          <p className="text-xs text-gray-500 italic">{row.deviceComment}</p>
                        </div>
                        <div className="col-span-1 text-right">
                          <p className="font-bold">{t('days_without_communication')}</p>
                          <p>{row.daysWithoutCommunication ? thousand(row.daysWithoutCommunication) : '0'}</p>
                        </div>
                      </div>
                    )
                  },
                  {
                    title: t('name'),
                    key: 'name',
                    align: 'center',
                    sorter: (a, b) => a?.deviceName?.localeCompare(b?.deviceName),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.deviceName}</span>
                    ),
                  },
                  {
                    title: t('comment'),
                    key: 'comment',
                    align: 'center',
                    sorter: (a, b) => a?.deviceComment?.localeCompare(b?.deviceComment),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.deviceComment}</span>
                    ),
                  },
                  {
                    title: t('days_without_communication'),
                    key: 'daysWithoutCommunication',
                    align: 'center',
                    sorter: (a, b) => a?.daysWithoutCommunication - b?.daysWithoutCommunication,
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.daysWithoutCommunication ? thousand(row.daysWithoutCommunication) : '0'}</span>
                    ),
                  },
                ]}
              />
            )
          }}
        />
      </div>

      <Divider className="col-span-1 md:col-span-2 lg:col-span-6 pt-12" orientation="left">{t('delayed_departures')}</Divider>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 flex">
        <Button
          className="bg-primaryColor px-2 ml-auto"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={(
            <div>
              <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
              {t('export')}
            </div>
          )}
          loading={loadingDelayedDeparturesData || loadingExportDelayedDeparturesData}
          onClick={() => getDelayedDeparturesDataExport()}
        />
      </div>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 mt-4">
        <div className="flex space-x-2 -mt-4">
          <div className="ml-auto mt-2">
            <strong>{t('view_percentage')}</strong>
            <Switch
              className="ml-1"
              checked={viewDelayedDeparturePercentage}
              onChange={(checked) => setViewDelayedDeparturePercentage(checked)}
            />
          </div>
          <RangePicker
            value={[
              dayjs(selectedDelayedDepartureDateRange?.start),
              dayjs(selectedDelayedDepartureDateRange?.end)
            ]}
            disabled={loadingDelayedDeparturesData}
            className="h-11 bg-transparent"
            onChange={(obj) => {
              setSelectedDelayedDepartureDateRange({
                ...selectedDelayedDepartureDateRange,
                start: obj[0],
                end: obj[1]
              });
            }}
            format="MMM DD, YYYY"
          />
        </div>
        <Segmented
          size="medium"
          block
          className="w-full my-2 space-x-2"
          options={[t('days'), t('routes')]}
          value={selectedDelayedDepartureGrouping}
          onChange={(value) => setSelectedDelayedDepartureGrouping(value)}
        />
        <Spin spinning={loadingDelayedDeparturesData}>
          <Bar
            height={350}
            data={{
              labels: _.map(selectedDelayedDepartureGrouping === t('days') ? _.sortBy(delayedDeparturesData.days, (g) => dayjs(g.date)) : _.sortBy(delayedDeparturesData.routes, (g) => dayjs(g.count)), (s) => s.label),
              datasets: [
                {
                  key: 'dataset',
                  label: t('delayed_departures_count'),
                  borderColor: '#0e733b',
                  backgroundColor: '#0e733baa',
                  data: _.map(selectedDelayedDepartureGrouping === t('days') ? _.sortBy(delayedDeparturesData.days, (g) => dayjs(g.date)) : _.sortBy(delayedDeparturesData.routes, (g) => dayjs(g.count)), (s) => viewDelayedDeparturePercentage ? s.percentage : s.count),
                }
              ],
            }}
            options={{
              maintainAspectRatio: false,
              scales: {
                y: {
                  ticks: {
                    beginAtZero: true,
                    callback: (value, index, ticks) => viewDelayedDeparturePercentage ? `${value}%` : thousand(value)
                  }
                },
              },
              responsive: true,
              plugins: {
                zoom: {
                  pan: {
                    enabled: true,
                    mode: 'x',
                  },
                  zoom: {
                    wheel: {
                      enabled: true,
                    },
                    pinch: {
                      enabled: true
                    },
                    mode: 'x',
                  }
                },
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
                datalabels: {
                  anchor: 'end',
                  offset: 8,
                  rotation: 330,
                  color: 'black',
                  formatter: (value, context) => viewDelayedDeparturePercentage ? `${value}%` : thousand(value),
                  labels: {
                    title: {
                      font: {
                        weight: 'regular'
                      }
                    }
                  }
                }
              },
            }}
          />
        </Spin>
      </div>
    
      <Divider className="col-span-1 md:col-span-2 lg:col-span-6 pt-12" orientation="left">{t('speeding')}</Divider>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 flex">
        <Button
          className="bg-primaryColor px-2 ml-auto"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={(
            <div>
              <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
              {t('export')}
            </div>
          )}
          loading={loadingSpeedingData || loadingExportSpeedingData}
          onClick={() => getSpeedingDataExport()}
        />
      </div>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 mt-4">
        <div className="flex -mt-4">
          <RangePicker
            value={[
              dayjs(selectedSpeedingDateRange?.start),
              dayjs(selectedSpeedingDateRange?.end)
            ]}
            disabled={loadingSpeedingData}
            className="h-11 bg-transparent ml-auto"
            onChange={(obj) => {
              setSelectedSpeedingDateRange({
                ...selectedSpeedingDateRange,
                start: obj[0],
                end: obj[1]
              });
            }}
            format="MMM DD, YYYY"
          />
        </div>
        <Segmented
          size="medium"
          block
          className="w-full mt-2 space-x-2"
          options={[t('days'), t('devices'), t('severity')]}
          value={selectedSpeedingGrouping}
          onChange={(value) => {
            setSelectedSpeedingGrouping(value);
          }}
        />
        <Table
          bordered
          rowKey="label"
          loading={loadingSpeedingData}
          dataSource={getSpeedingTableData()}
          columns={[
            {
              title: '',
              align: 'left',
              className: 'text-sm w-full md:hidden',
              render: (row) => (
                <div className="grid grid-cols-2 gap-2 w-full text-center">
                  <div className="col-span-1 text-left">
                    {selectedSpeedingGrouping === t('days') && (<p className="font-bold">{t('date')}</p>)}
                    {selectedSpeedingGrouping === t('devices') && (<p className="font-bold">{t('device')}</p>)}
                    {selectedSpeedingGrouping === t('severity') && (<p className="font-bold">{t('severity')}</p>)}
                    <p>{row.label}</p>
                  </div>
                  <div className="col-span-1 text-right">
                    <p className="font-bold">{t('count')}</p>
                    <p>{thousand(row.count)}</p>
                  </div>
                </div>
              )
            },
            {
              title: t('date'),
              key: 'date',
              align: 'center',
              sorter: (a, b) => dayjs(a?.date) - dayjs(b?.date),
              className: `text-sm hidden ${selectedSpeedingGrouping === t('days') ? 'md:table-cell' : ''}`,
              render: (row) => (
                <span>{row.label}</span>
              ),
            },
            {
              title: t('device'),
              key: 'device',
              align: 'center',
              sorter: (a, b) => a?.deviceName?.localeCompare(b?.deviceName),
              className: `text-sm hidden ${selectedSpeedingGrouping === t('devices') ? 'md:table-cell' : ''}`,
              render: (row) => (
                <span>{row.label}</span>
              ),
            },
            {
              title: t('severity'),
              key: 'device',
              align: 'center',
              sorter: (a, b) => a?.deviceName?.localeCompare(b?.deviceName),
              className: `text-sm hidden ${selectedSpeedingGrouping === t('severity') ? 'md:table-cell' : ''}`,
              render: (row) => (
                <span>{row.label}</span>
              ),
            },
            {
              title: t('count'),
              key: 'count',
              align: 'center',
              sorter: (a, b) => a.count - b.count,
              className: 'text-sm hidden md:table-cell',
              render: (row) => (
                <span>{thousand(row.count)}</span>
              ),
            },
          ]}
          expandable={{
            expandRowByClick: false,
            rowExpandable: (record) => record.incidents?.length > 0,
            expandedRowRender: (record, index) => (
              <Table
                dataSource={record.incidents}
                bordered
                size="small"
                columns={[
                  {
                    title: '',
                    align: 'left',
                    className: 'text-sm w-full md:hidden',
                    render: (row) => (
                      <div className="grid grid-cols-2 gap-2 w-full text-center">
                        <div className="col-span-1 text-left">
                          <p className="font-bold">{t('date')}</p>
                          <p>{dayjs(row.date).format('MMM DD, YYYY hh:mm A')}</p>
                        </div>
                        <div className="col-span-1 text-right">
                          <p className="font-bold">{t('name')}</p>
                          <p>{row.deviceName}</p>
                          <p className="text-xs text-gray-500 italic">{row.deviceComment}</p>
                        </div>
                        <div className="col-span-1 text-left">
                          <p className="font-bold">{t('location')}</p>
                          <button className="link-button text-blue-400" type="button" onClick={() => setSelectedCoords({ lat: row.latitude, lng: row.longitude })}>{row.latitude}, {row.longitude}</button>
                        </div>
                        <div className="col-span-1 text-right">
                          <p className="font-bold">{t('severity')}</p>
                          <p>{row.severity}</p>
                        </div>
                        <div className="col-span-1 text-left">
                          <p className="font-bold">{t('speed')}</p>
                          <p>{showMph ? convertKmhToMph(row.velocityKmh) : row.velocityKmh} {showMph ? 'MPH' : 'KMH'}</p>
                        </div>
                        <div className="col-span-1 text-right">
                          <p className="font-bold">{t('excess')}</p>
                          <p>{showMph ? convertKmhToMph(row.excessKmh) : row.excessKmh} {showMph ? 'MPH' : 'KMH'}</p>
                        </div>
                      </div>
                    )
                  },
                  {
                    title: t('date'),
                    key: 'date',
                    align: 'center',
                    sorter: (a, b) => dayjs(a?.date) - dayjs(b?.date),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{dayjs(row.date).format('MMM DD, YYYY hh:mm A')}</span>
                    ),
                  },
                  {
                    title: t('name'),
                    key: 'name',
                    align: 'center',
                    sorter: (a, b) => a?.deviceName?.localeCompare(b?.deviceName),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.deviceName}</span>
                    ),
                  },
                  {
                    title: t('comment'),
                    key: 'comment',
                    align: 'center',
                    sorter: (a, b) => a?.deviceComment?.localeCompare(b?.deviceComment),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.deviceComment}</span>
                    ),
                  },
                  {
                    title: t('location'),
                    key: 'location',
                    align: 'center',
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <button className="link-button text-blue-400" type="button" onClick={() => setSelectedCoords({ lat: row.latitude, lng: row.longitude })}>{row.latitude}, {row.longitude}</button>
                    ),
                  },
                  {
                    title: t('severity'),
                    key: 'severity',
                    align: 'center',
                    sorter: (a, b) => a?.severity?.localeCompare(b?.severity),
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{row.severity}</span>
                    ),
                  },
                  {
                    title: t('speed'),
                    key: 'speed',
                    align: 'center',
                    sorter: (a, b) => a?.velocityKmh - b?.velocityKmh,
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{showMph ? convertKmhToMph(row.velocityKmh) : row.velocityKmh} {showMph ? 'MPH' : 'KMH'}</span>
                    ),
                  },
                  {
                    title: t('excess'),
                    key: 'speed',
                    align: 'center',
                    sorter: (a, b) => a?.excessKmh - b?.excessKmh,
                    className: 'text-sm hidden md:table-cell',
                    render: (row) => (
                      <span>{showMph ? convertKmhToMph(row.excessKmh) : row.excessKmh} {showMph ? 'MPH' : 'KMH'}</span>
                    ),
                  },
                ]}
              />
            )
          }}
        />
      </div>

      <Divider className="col-span-1 md:col-span-2 lg:col-span-6 pt-12" orientation="left">{t('arrival_vs_departures')}</Divider>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 flex">
        <Button
          className="bg-primaryColor px-2 ml-auto"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={(
            <div>
              <FontAwesomeIcon className="mr-2" icon={faCloudDownload} />
              {t('export')}
            </div>
          )}
          loading={loadingArrivalDeparturesData || loadingExportArrivalDeparturesData}
          onClick={() => getArrivalDeparturesDataExport()}
        />
      </div>
      <div className="col-span-1 md:col-span-2 lg:col-span-6 mt-4">
        <div className="flex space-x-2 -mt-4">
          <RangePicker
            value={[
              dayjs(selectedArrivalDepartureDateRange?.start),
              dayjs(selectedArrivalDepartureDateRange?.end)
            ]}
            disabled={loadingArrivalDeparturesData}
            className="h-11 bg-transparent ml-auto"
            onChange={(obj) => {
              setSelectedArrivalDepartureDateRange({
                ...selectedArrivalDepartureDateRange,
                start: obj[0],
                end: obj[1]
              });
            }}
            format="MMM DD, YYYY"
          />
        </div>
        <Spin spinning={loadingArrivalDeparturesData}>
          <Bar
            height={350}
            data={{
              labels: _.map(arrivalDeparturesData, (s) => s.label),
              datasets: [
                {
                  key: 'arrivals',
                  label: t('arrival_count'),
                  borderColor: '#0e733b',
                  backgroundColor: '#0e733baa',
                  data: _.map(arrivalDeparturesData, (s) => s.arrivalCount),
                },
                {
                  key: 'departures',
                  label: t('departure_count'),
                  borderColor: '#0e733b',
                  backgroundColor: '#63be3baa',
                  data: _.map(arrivalDeparturesData, (s) => s.departureCount),
                }
              ],
            }}
            options={{
              maintainAspectRatio: false,
              scales: {
                y: {
                  ticks: {
                    beginAtZero: true,
                    callback: (value, index, ticks) => thousand(value)
                  }
                },
              },
              responsive: true,
              plugins: {
                zoom: {
                  pan: {
                    enabled: true,
                    mode: 'x',
                  },
                  zoom: {
                    wheel: {
                      enabled: true,
                    },
                    pinch: {
                      enabled: true
                    },
                    mode: 'x',
                  }
                },
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
                datalabels: {
                  anchor: 'end',
                  offset: 8,
                  rotation: 330,
                  color: 'black',
                  formatter: (value, context) => thousand(value),
                  labels: {
                    title: {
                      font: {
                        weight: 'regular'
                      }
                    }
                  }
                }
              },
            }}
          />
        </Spin>
      </div>

      <Modal
        title={t('location')}
        open={selectedCoords.lon !== 0 && selectedCoords.lng !== 0}
        onCancel={() => {
          setSelectedCoords({ lon: 0, lng: 0 });
        }}
        footer={[
          <Button
            key="closecordsmodalbutton"
            type="primary"
            danger
            className="bg-rose-500 px-4"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            onClick={() => setSelectedCoords({ lon: 0, lng: 0 })}
            text={t('close')}
          />
        ]}
      >
        <div className="flex">
          <div className="mx-auto w-96 h-96">
            <Map
              mapId="coordsMap"
              center={selectedCoords}
              zoom={mapZoom}
              onZoomChanged={(ev) => setMapZoom(ev.detail.zoom)}
            >
              <Marker
                position={selectedCoords}
              />
            </Map>
          </div>
        </div>
      </Modal>
    </div>
  );
}


export default AmaDashboardAnalytics;
